/**
 * custom-kit2-pdp.js
 *
 * Pixyls custom kit builder:
 *  - Photo upload → pixelated square-sticker preview
 *  - Grid sized by 1 inch = 10 sticker squares formula
 *  - Variant size change → resize + re-render
 *  - Passes original photo + pixelated production file to cart
 *  - Zoom controls on preview canvases
 */

(function () {
  'use strict';

  var zoomLevel = 1;
  var ZOOM_MIN = 0.5, ZOOM_MAX = 3, ZOOM_STEP = 0.25;
  var uploadedImageDataUrl = null;
  var currentSizeStr = null;   // e.g. "8x10"

  var gridMap = {};    // populated from variant metafield custom.grid_size
  var colorMap = {};   // populated from variant metafield custom.max_colors

  var CLOUDINARY_CLOUD  = 'dbpailcjd';
  var CLOUDINARY_PRESET = 'ml_default';

  var section;
  var fileInput, uploadDifferentBtn;
  var canvasBlocks, previewCanvases;
  var postUploadBlock;
  var uploadingIndicator;
  var pixelatedInput; // hidden file input — carries the pixelated production image

  // ── Init ──────────────────────────────────────────────────
  function init() {
    section = document.querySelector('[data-cdp-section]');
    if (!section) return;

    fileInput          = section.querySelector('input[type="file"]');
    uploadDifferentBtn = section.querySelector('#cdp-upload-different');
    canvasBlocks       = section.querySelectorAll('.cdp-canvas-block');
    previewCanvases    = section.querySelectorAll('canvas[id="cdp-preview-canvas"]');
    postUploadBlock    = section.querySelector('#cdp-post-upload');
    uploadingIndicator = section.querySelector('#cdp-uploading');

    // Load variant grid-size metafield map rendered by Liquid
    var gridMapEl = section.querySelector('#cdp-grid-map');
    if (gridMapEl) {
      try { gridMap = JSON.parse(gridMapEl.textContent); } catch (e) {}
    }
    var colorMapEl = section.querySelector('#cdp-color-map');
    if (colorMapEl) {
      try { colorMap = JSON.parse(colorMapEl.textContent); } catch (e) {}
    }

    // Read preselected variant size (Shopify renders this server-side)
    currentSizeStr = getSelectedSize();
    if (currentSizeStr) {
      previewCanvases.forEach(function (c) { setCanvasDims(c, currentSizeStr); });
    }

    // Hidden text input — stores Cloudinary PNG URL as a plain line item property.
    var productForm = section.querySelector('.product__form');
    if (productForm) {
      pixelatedInput = document.createElement('input');
      pixelatedInput.type  = 'hidden';
      pixelatedInput.name  = 'properties[Pixelated Preview]';
      pixelatedInput.value = '';
      productForm.appendChild(pixelatedInput);
    }

    bindEvents();
  }

  // Returns the currently selected size string that matches "NxN" (inches)
  function getSelectedSize() {
    // Radio-style variant selector
    var checked = section.querySelector('[data-single-option-selector]:checked');
    if (checked && /\d+\s*x\s*\d+/i.test(checked.value)) return checked.value;

    // Popout / hidden-input-style variant selector
    var inputs = section.querySelectorAll('input[data-single-option-selector]');
    for (var i = 0; i < inputs.length; i++) {
      if (/\d+\s*x\s*\d+/i.test(inputs[i].value)) return inputs[i].value;
    }
    return null;
  }

  // ── Events ────────────────────────────────────────────────
  function bindEvents() {
    if (uploadDifferentBtn) uploadDifferentBtn.addEventListener('click', triggerUpload);
    if (fileInput)          fileInput.addEventListener('change', handleFileChange);

    // Variant size change — hidden input updated by Broadcast theme
    section.addEventListener('change', function (e) {
      if (!e.target.hasAttribute('data-single-option-selector')) return;
      var val = e.target.value;
      if (!/\d+\s*x\s*\d+/i.test(val)) return;
      onVariantSizeChange(val);
    });

    // Popout option click — fallback for when change event doesn't bubble
    section.addEventListener('click', function (e) {
      var option = e.target.closest('[data-popout-option]');
      if (!option) return;
      var val = option.dataset.value;
      if (!val || !/\d+\s*x\s*\d+/i.test(val)) return;
      onVariantSizeChange(val);
    });

    // ── hasFileInput bypass ────────────────────────────────
    // Theme's product form JS blocks cart add when any input[type="file"]
    // exists inside the form (theme.dev.js:9011-9013).
    // We temporarily override form.querySelector so the check returns null
    // while new FormData(form) still picks up both file inputs normally.
    var productForm = section.querySelector('.product__form');
    var atcBtn = section.querySelector('[data-add-to-cart]');
    if (atcBtn && productForm) {
      atcBtn.addEventListener('click', function () {
        var orig = productForm.querySelector.bind(productForm);
        productForm.querySelector = function (sel) {
          if (typeof sel === 'string' && sel.indexOf('file') !== -1) return null;
          return orig(sel);
        };
        // Block sticky bar / secondary ATC buttons from firing duplicate add
        document.querySelectorAll('[data-add-to-cart]').forEach(function (btn) {
          if (btn !== atcBtn) btn.setAttribute('disabled', 'disabled');
        });
        setTimeout(function () {
          productForm.querySelector = orig;
          document.querySelectorAll('[data-add-to-cart]').forEach(function (btn) {
            if (btn !== atcBtn) btn.removeAttribute('disabled');
          });
        }, 3000);
      }, true);
    }

    // Zoom — duplicate IDs in markup (desktop + mobile)
    section.querySelectorAll('[id="cdp-zoom-in"]').forEach(function (btn) {
      btn.addEventListener('click', handleZoomIn);
    });
    section.querySelectorAll('[id="cdp-zoom-out"]').forEach(function (btn) {
      btn.addEventListener('click', handleZoomOut);
    });
  }

  function triggerUpload() {
    if (fileInput) fileInput.click();
  }

  // ── File upload ───────────────────────────────────────────
  function handleFileChange(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }

    setUploading(true);

    var reader = new FileReader();
    reader.onload = function (evt) {
      uploadedImageDataUrl = evt.target.result;
      currentSizeStr = getSelectedSize();
      if (currentSizeStr) {
        previewCanvases.forEach(function (c) { setCanvasDims(c, currentSizeStr); });
      }
      renderAllCanvases(uploadedImageDataUrl, function () {
        setUploading(false);
        revealPostUpload();
      });
    };
    reader.readAsDataURL(file);
  }

  function setUploading(state) {
    if (uploadingIndicator) uploadingIndicator.classList.toggle('is-active', state);
  }

  // ── Variant size change ───────────────────────────────────
  function onVariantSizeChange(val) {
    currentSizeStr = val;
    previewCanvases.forEach(function (c) { setCanvasDims(c, val); });
    if (uploadedImageDataUrl) {
      renderAllCanvases(uploadedImageDataUrl, null);
    }
  }

  // ── Size helpers ──────────────────────────────────────────
  function parseSize(str) {
    var m = str.match(/(\d+)\s*x\s*(\d+)/i);
    return m ? { w: parseInt(m[1], 10), h: parseInt(m[2], 10) } : { w: 1, h: 1 };
  }

  // Sets display canvas dimensions preserving aspect ratio at 800px base
  function setCanvasDims(canvas, sizeStr) {
    var dims  = parseSize(sizeStr);
    var BASE  = 800;
    var ratio = dims.h / dims.w;
    if (dims.w >= dims.h) {
      canvas.width  = BASE;
      canvas.height = Math.round(BASE * ratio);
    } else {
      canvas.height = BASE;
      canvas.width  = Math.round(BASE / ratio);
    }
  }

  // Returns grid dimensions: metafield value if set, else 1 inch = 10 squares fallback
  function getGridDims(sizeStr) {
    var override = gridMap[sizeStr];
    if (override && /\d+\s*x\s*\d+/i.test(override)) return parseSize(override);
    var dims = parseSize(sizeStr);
    return { w: dims.w * 10, h: dims.h * 10 };
  }

  // ── Canvas render ─────────────────────────────────────────
  function renderAllCanvases(dataUrl, callback) {
    var arr = Array.from(previewCanvases);
    if (!arr.length) { if (callback) callback(); return; }
    arr.forEach(function (canvas, i) {
      renderPixelPreview(canvas, dataUrl, i === arr.length - 1 ? callback : null);
    });
  }

  function renderPixelPreview(canvas, dataUrl, callback) {
    var ctx = canvas.getContext('2d');
    if (!ctx) { if (callback) callback(); return; }

    var img = new Image();
    img.onload = function () {
      var CW = canvas.width;
      var CH = canvas.height;

      // Grid = metafield override or fallback 1 inch = 10 sticker squares
      var grid   = getGridDims(currentSizeStr || '8x10');
      var GRID_W = Math.max(1, grid.w);
      var GRID_H = Math.max(1, grid.h);

      // Cover: fill the grid, center-crop any overflow (no white bars).
      var imageRatio = img.width / img.height;
      var gridRatio  = GRID_W / GRID_H;
      var drawW, drawH, dx, dy;
      if (imageRatio > gridRatio) {
        // Image wider than grid — scale by height, crop sides
        drawH = GRID_H;
        drawW = Math.round(GRID_H * imageRatio);
        dx    = Math.round((GRID_W - drawW) / 2); // negative → crops
        dy    = 0;
      } else {
        // Image taller than grid — scale by width, crop top/bottom
        drawW = GRID_W;
        drawH = Math.round(GRID_W / imageRatio);
        dx    = 0;
        dy    = Math.round((GRID_H - drawH) / 2); // negative → crops
      }

      // Step 1: shrink full image to sticker grid resolution.
      // smoothing ON here → browser averages colours per sticker cell correctly.
      var off    = document.createElement('canvas');
      off.width  = GRID_W;
      off.height = GRID_H;
      var offCtx = off.getContext('2d');
      offCtx.fillStyle = '#ffffff';
      offCtx.fillRect(0, 0, GRID_W, GRID_H);
      offCtx.imageSmoothingEnabled = true;
      offCtx.drawImage(img, 0, 0, img.width, img.height, dx, dy, drawW, drawH);

      // Step 2: reduce to per-variant max colours (metafield), fallback 100.
      var maxColors = colorMap[currentSizeStr] || 100;
      medianCutQuantize(offCtx, GRID_W, GRID_H, maxColors);

      // Export: first canvas only — upload PNG to Cloudinary, store URL as line item property.
      if (pixelatedInput && canvas === previewCanvases[0]) {
        pixelatedInput.disabled = true; // exclude from form until URL is ready
        var fd = new FormData();
        fd.append('file',          off.toDataURL('image/png'));
        fd.append('upload_preset', CLOUDINARY_PRESET);
        fetch('https://api.cloudinary.com/v1_1/' + CLOUDINARY_CLOUD + '/image/upload', {
          method: 'POST',
          body:   fd
        })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.secure_url) {
            pixelatedInput.value    = data.secure_url;
            pixelatedInput.disabled = false;
          }
        })
        .catch(function () {});
      }

      // Step 3: draw each sticker square as a solid filled rectangle.
      // fillRect per cell guarantees one flat colour per square — no edge blending.
      var gridData = offCtx.getImageData(0, 0, GRID_W, GRID_H).data;
      var cellW = CW / GRID_W;
      var cellH = CH / GRID_H;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, CW, CH);
      for (var gy = 0; gy < GRID_H; gy++) {
        for (var gx = 0; gx < GRID_W; gx++) {
          var pi = (gy * GRID_W + gx) * 4;
          ctx.fillStyle = 'rgb(' + gridData[pi] + ',' + gridData[pi+1] + ',' + gridData[pi+2] + ')';
          ctx.fillRect(
            Math.round(gx * cellW),
            Math.round(gy * cellH),
            Math.round((gx + 1) * cellW) - Math.round(gx * cellW),
            Math.round((gy + 1) * cellH) - Math.round(gy * cellH)
          );
        }
      }

      applyZoom(canvas);
      if (callback) callback();
    };
    img.src = dataUrl;
  }

  // ── Median-cut colour quantization ───────────────────────
  // Reduces the off-screen grid canvas to ≤ maxColors unique colours.
  // Each unique colour corresponds to one sticker colour in the physical kit.
  function medianCutQuantize(ctx, w, h, maxColors) {
    var imageData = ctx.getImageData(0, 0, w, h);
    var data = imageData.data;
    var total = w * h;

    // Build pixel index array — work on indices to avoid heavy object allocation
    var idx = new Int32Array(total);
    for (var i = 0; i < total; i++) idx[i] = i;

    // Bucket = slice of idx array [start, end)
    var buckets = [{ start: 0, end: total }];

    // Helper: channel range within a bucket
    function bucketRange(b) {
      var rMin=255,rMax=0,gMin=255,gMax=0,bMin=255,bMax=0;
      for (var i = b.start; i < b.end; i++) {
        var p = idx[i] * 4;
        var r=data[p],g=data[p+1],bl=data[p+2];
        if(r<rMin)rMin=r; if(r>rMax)rMax=r;
        if(g<gMin)gMin=g; if(g>gMax)gMax=g;
        if(bl<bMin)bMin=bl; if(bl>bMax)bMax=bl;
      }
      var rr=rMax-rMin, gr=gMax-gMin, br=bMax-bMin;
      var ch = (rr>=gr&&rr>=br)?0:(gr>=br?1:2);
      return { ch: ch, range: Math.max(rr,gr,br) };
    }

    while (buckets.length < maxColors) {
      // Find bucket with largest colour range
      var splitIdx = -1, maxRange = -1, splitCh = 0;
      for (var b = 0; b < buckets.length; b++) {
        var bkt = buckets[b];
        if (bkt.end - bkt.start < 2) continue;
        var info = bucketRange(bkt);
        if (info.range > maxRange) { maxRange=info.range; splitIdx=b; splitCh=info.ch; }
      }
      if (splitIdx === -1 || maxRange === 0) break;

      var bkt = buckets[splitIdx];
      var ch = splitCh;

      // Sort the idx slice by the chosen channel (insertion sort — buckets are small)
      var slice = idx.subarray(bkt.start, bkt.end);
      slice.sort(function(a, b) { return data[a*4+ch] - data[b*4+ch]; });

      var mid = bkt.start + Math.floor((bkt.end - bkt.start) / 2);
      buckets.splice(splitIdx, 1,
        { start: bkt.start, end: mid },
        { start: mid,       end: bkt.end }
      );
    }

    // Compute average colour per bucket → palette
    var palette = buckets.map(function(bkt) {
      var r=0,g=0,b=0, n=bkt.end-bkt.start;
      for (var i = bkt.start; i < bkt.end; i++) {
        var p = idx[i]*4;
        r+=data[p]; g+=data[p+1]; b+=data[p+2];
      }
      return [Math.round(r/n), Math.round(g/n), Math.round(b/n)];
    });

    // Map every pixel to its bucket's average colour (bucket membership already encoded by idx order)
    for (var b = 0; b < buckets.length; b++) {
      var col = palette[b];
      for (var i = buckets[b].start; i < buckets[b].end; i++) {
        var p = idx[i]*4;
        data[p]=col[0]; data[p+1]=col[1]; data[p+2]=col[2];
      }
    }

    ctx.putImageData(imageData, 0, 0);
  }

  // ── Export pixelated production image to hidden file input ─
  // Uses the display canvas (800px) — each sticker square is clearly
  // visible as a coloured block. This is what the production team works from.
  // ── Zoom ──────────────────────────────────────────────────
  function applyZoom(canvas) {
    canvas.style.transform = 'scale(' + zoomLevel + ')';
  }
  function handleZoomIn() {
    zoomLevel = Math.min(ZOOM_MAX, +(zoomLevel + ZOOM_STEP).toFixed(2));
    previewCanvases.forEach(applyZoom);
  }
  function handleZoomOut() {
    zoomLevel = Math.max(ZOOM_MIN, +(zoomLevel - ZOOM_STEP).toFixed(2));
    previewCanvases.forEach(applyZoom);
  }

  // ── Reveal post-upload UI ─────────────────────────────────
  function revealPostUpload() {
    canvasBlocks.forEach(function (block) {
      block.classList.add('is-visible');
      block.setAttribute('aria-hidden', 'false');
    });
    if (postUploadBlock) {
      postUploadBlock.classList.add('is-visible');
      postUploadBlock.setAttribute('aria-hidden', 'false');
    }
    var desktopBlock = section.querySelector('.cdp-canvas-block.canvas-desktop');
    if (desktopBlock) {
      setTimeout(function () {
        desktopBlock.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 80);
    }
  }

  // ── Boot ──────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('[data-cdp-section]')) init();
  });

})();
